import { VStack, useDisclosure } from '@chakra-ui/react';
import { useState, useMemo, Dispatch, SetStateAction } from 'react';
import { Table, LearnMoreDrawer, MetricLearnMoreHeader } from 'Molecules';
import {
  checkMateriality,
  handleMetric,
  MfDisclosureRequirementTableMetric,
} from './DrMateriality.hooks';
import { getMetricRefNumber } from 'containers/Esrs/pieces/DisclosureRequirements/Metrics/Metrics.hooks';
import { TableFilters } from '../../DataPointsBank/BrowserTableComponents/TableFilters';
import { useTableColumns } from '../../DataPointsBank/BrowserTableComponents/useTableColumns';
import { useTableFilters } from '../../DataPointsBank/BrowserTableComponents/useTableFilters';
import { Metric } from '../../MaterialityAssessment.d';
import {
  COLLECT_ONLY_OPTIONS,
  METRIC_MATERIALITY_OPTIONS,
  VSME_METRIC_MATERIALITY_OPTIONS,
} from './DrMaterialityUtils';
import { MMOmitAggregate } from './DrMateriality';
import {
  ParentMaterialMetrics,
  Materiality,
  MaterialityState,
  MetricMaterialityState,
  MappedDisclosureRequirements,
} from '../DoubleMaterialityAssessment.d';
import { FrameworkRefs, Frameworks } from 'containers/Esrs/pieces/Assessment';
import { GetDrMaterialMetricsQuery_ } from 'models';
import { SearchEmptyState } from 'Atoms';
import { DataCollectionLevel } from 'containers/Esrs/pieces/DataCollection';

type MFDatapointsBrowserTableProps = {
  mfData: MfDisclosureRequirementTableMetric[];
  materialMetrics: MMOmitAggregate[];
  isCollectOnly: boolean;
  isDrAssessed: boolean;
  parentMetrics: ParentMaterialMetrics;
  parentMateriality?: Materiality | MaterialityState;
  framework: FrameworkRefs;
  setMaterialMetrics: Dispatch<SetStateAction<GetDrMaterialMetricsQuery_['esrs_MaterialMetric']>>;
  hasPhasedInOption: boolean;
  disclosureRequirement?: MappedDisclosureRequirements[number];
  setMaterialMetricsToDelete: Dispatch<SetStateAction<string[]>>;
};

export const MFDatapointsBrowserTable = ({
  mfData,
  materialMetrics,
  isCollectOnly,
  isDrAssessed,
  parentMetrics,
  parentMateriality,
  framework,
  setMaterialMetrics,
  hasPhasedInOption,
  disclosureRequirement,
  setMaterialMetricsToDelete,
}: MFDatapointsBrowserTableProps) => {
  const { isOpen, onClose, onOpen } = useDisclosure();
  const [selectedMetric, setSelectedMetric] = useState<Metric>();

  const selectOptions = useMemo(() => {
    if (isCollectOnly) {
      return COLLECT_ONLY_OPTIONS;
    }
    return (
      framework === FrameworkRefs.vsme
        ? VSME_METRIC_MATERIALITY_OPTIONS
        : METRIC_MATERIALITY_OPTIONS
    ).filter((opt) => (!hasPhasedInOption ? opt.value !== MetricMaterialityState.phasedIn : true));
  }, [isCollectOnly, hasPhasedInOption]);

  const filterableData = useMemo(() => mfData.filter((m) => m.metric.isAssessable), [mfData]);

  const filters = useTableFilters(filterableData);

  const handleUpdateMateriality = (data: {
    selection: MetricMaterialityState;
    metric: Metric;
    isChild?: boolean;
  }) => {
    const { selection, metric, isChild } = data;
    const isMaterial = checkMateriality(selection, isCollectOnly);
    setMaterialMetrics((prevMetrics) => {
      const foundMetric = prevMetrics.find((m) => m.metricRef === metric.reference);
      const isDataGatheringOnly =
        selection === MetricMaterialityState.gatherData ||
        selection === MetricMaterialityState.phasedIn;
      const isPhasedIn = selection === MetricMaterialityState.phasedIn;

      setMaterialMetricsToDelete((prev) => {
        const foundMetric = materialMetrics.find((m) => m.metricRef === metric.reference);
        if (foundMetric?.id && prev.includes(foundMetric.id)) {
          return prev.filter((id) => id !== foundMetric.id);
        }
        return prev;
      });

      if (foundMetric) {
        if (isChild) {
          if (!metric.isAssessable) {
            const isAnyParentMaterial = metric.parentMetrics?.some(
              (parentMet) =>
                prevMetrics.find((m) => m.metricRef === parentMet.parentMetric.reference)
                  ?.isMaterial
            );
            if (isAnyParentMaterial && foundMetric.isMaterial && !foundMetric.isDataGatheringOnly) {
              return [...prevMetrics];
            }
            return prevMetrics.map((m) =>
              m.metricRef === metric.reference
                ? {
                    ...m,
                    isMaterial,
                    isDataGatheringOnly,
                    isPhasedIn,
                    metric,
                  }
                : m
            );
          }
          if (
            (foundMetric.isMaterial && !foundMetric.isDataGatheringOnly) ||
            selection === MetricMaterialityState.notMaterial
          ) {
            return [...prevMetrics];
          }
        }
        return prevMetrics.map((m) =>
          m.metricRef === metric.reference
            ? {
                ...m,
                isMaterial,
                isDataGatheringOnly,
                isPhasedIn,
                metric,
              }
            : m
        );
      } else {
        return [
          ...prevMetrics,
          {
            metricRef: metric.reference,
            isMaterial,
            isDataGatheringOnly,
            isPhasedIn,
            metric,
          },
        ];
      }
    });
    //handle children
    if ((metric.childrenMetrics?.length ?? 0) > 0) {
      metric.childrenMetrics.forEach((child) => {
        if (child.childMetric)
          handleUpdateMateriality({
            selection,
            metric: child.childMetric as Metric,
            isChild: true,
          });
      });
    }
  };

  const handleDeleteMetric = (metric: Metric) => {
    // Helper to collect all metric references to delete (including children)
    const collectMetricRefs = (m: Metric): string[] => {
      let refs = [m.reference];
      if (m.childrenMetrics && (m.childrenMetrics?.length ?? 0) > 0) {
        m.childrenMetrics.forEach((child) => {
          if (child.childMetric) {
            refs = refs.concat(collectMetricRefs(child.childMetric as Metric));
          }
        });
      }
      return refs;
    };

    const metricRefsToDelete = collectMetricRefs(metric);

    // Get the existing materialMetrics ids before filtering them out
    const idsToDelete = materialMetrics
      .filter((mm) => metricRefsToDelete.includes(mm.metricRef))
      .map((mm) => mm.id)
      .filter((id): id is string => typeof id === 'string');

    setMaterialMetricsToDelete((prev) => [...prev, ...idsToDelete]);

    setMaterialMetrics((prev) => {
      return prev.filter((m) => !metricRefsToDelete.includes(m.metricRef));
    });
  };

  const getMateriality = (metric: Metric) => {
    return handleMetric(
      metric,
      materialMetrics,
      isDrAssessed,
      isCollectOnly,
      parentMetrics,
      parentMateriality,
      framework
    );
  };

  const isDataCollectionGroup = (metric: Metric, parentMetrics: ParentMaterialMetrics): boolean => {
    if (!metric.childrenMetrics?.length) {
      const parentMaterialMetric = parentMetrics.find((met) => met.metricRef === metric.reference);

      return parentMaterialMetric?.dataCollection === DataCollectionLevel.group;
    }
    return (
      metric.childrenMetrics?.every((met) => {
        return met.childMetric && isDataCollectionGroup(met.childMetric as Metric, parentMetrics);
      }) ?? false
    );
  };

  const getParentMetricMaterialityAndDataCollection = (metric: Metric) => {
    const parentMaterialMetric = parentMetrics.find((met) => met.metricRef === metric.reference);

    const isParentMetricMaterial =
      parentMaterialMetric?.isMaterial && parentMateriality !== MaterialityState.notMaterial;

    const isParentMetricGroupLevel = isDataCollectionGroup(metric, parentMetrics);

    return { isParentMetricMaterial, isParentMetricGroupLevel };
  };

  const getReference = (metric: Metric) => {
    return `${metric.disclosureRequirementRef} ${getMetricRefNumber(metric)}`;
  };

  const columns = useTableColumns({
    framework,
    selectOptions,
    onUpdateMateriality: handleUpdateMateriality,
    onDeleteMetric: (metric) => handleDeleteMetric(metric),
    onOpenLearnMore: (metric) => {
      setSelectedMetric(metric);
      onOpen();
    },
    getMateriality,
    getReference,
    isNotAssessedOrNotMaterial: false,
    isDrView: true,
    getParentMetricMaterialityAndDataCollection,
  });

  const selectedMetricFromDR = useMemo(() => {
    return disclosureRequirement?.metrics.find(
      (metric) => metric.reference === selectedMetric?.reference
    );
  }, [selectedMetric, disclosureRequirement]);

  return (
    <VStack gap="16px" alignItems="start" width={'100%'}>
      <TableFilters {...filters} isDrView={true} />
      {(filters.filteredData?.length ?? 0) > 0 ? (
        <Table
          data={filters.filteredData}
          columns={columns}
          cellProps={{ padding: '2px 8px 2px 8px' }}
          headerPadding="2px 8px 2px 8px"
          conditionalRowProps={(row) =>
            row.framework === framework ? { background: 'bg.disabled' } : {}
          }
        />
      ) : (
        <SearchEmptyState setSearch={filters.setSearchValue} />
      )}

      <LearnMoreDrawer
        isOpen={isOpen}
        onClose={onClose}
        description={selectedMetric?.description ?? ''}
        header={!selectedMetric ? disclosureRequirement?.title : undefined}
        customHeader={
          selectedMetric ? (
            <MetricLearnMoreHeader
              metricRef={selectedMetric?.reference ?? ''}
              tags={selectedMetricFromDR?.tags ?? []}
              frameworkName={
                Frameworks[
                  selectedMetric?.requirement?.group?.standard
                    ?.frameworkRef as keyof typeof FrameworkRefs
                ]
              }
            />
          ) : undefined
        }
      />
    </VStack>
  );
};
